import { Component, HostBinding, inject, Input, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, delay, takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/common/components/base.component';
import { CustomDefaultPlaceCategory } from 'src/app/modules/shared/models/custom.default.place.category';
import { PlaceType } from 'src/app/modules/shared/models/place.type';
import { frontendSettingsSelector } from 'src/app/modules/shared/ngrx.stores/frontend.settings/selectors';
import {
  IAppState,
  IFrontendSettings,
} from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import * as PlaceQueryCommandActions from 'src/app/modules/place-calendar/ngrx.stores/place.query.command/actions';
import { placeQueryCommandSelector } from '../../ngrx.stores/place.query.command/selectors';
import { CalendarQueryCommand } from 'src/app/modules/shared/models/calendar.action.command';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { PlaceStore } from '../../ngrx.stores/place.query.command/stores';
import { Location } from '@angular/common';
import { VisitorService } from '../../../shared/services/visitor.service';
import { ConfigService } from 'src/app/common/services/config/config.service';
import { PendoService } from 'src/app/modules/shared/services/pendo.service';

@Component({
  selector: 'ig-custom-default-place-category-filter',
  templateUrl: './custom.default.place.category.filter.component.html',
  styleUrls: ['./custom.default.place.category.filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CustomDefaultPlaceCategoryFilterComponent extends BaseComponent {
  @HostBinding('class') hostClass = 'ig-custom-default-category';
  customDefaultPlaceCategory: CustomDefaultPlaceCategory | null = null;
  @Input() mode: 'expand' | 'dropdown' = 'dropdown';
  frontendSettings$: Observable<IFrontendSettings | null>;
  settings: IFrontendSettings | null = null;
  title = '';

  @Input() isOnlyShowNodeFromUrl: boolean = false; // for nearby places component, it will show all categories in the dropdown, so this value should be false
  @Input() isTriggerCalendarReload: boolean = true; // on topbar/sidebar, it needs to reload the calendar. But on nearby place component, it will not reload the calendar, only reload nearby places. In this case it will not trigger NgRx dispatch
  node: PlaceType | null = null;
  urlRootNode: PlaceType | null = null;
  private initialCustomPlacesId: string | null = null;
  private hasUserSelectedCategory = false;

  isLoading: boolean = false;

  pendoService = inject(PendoService);

  constructor(
    public activatedRoute: ActivatedRoute,
    public placeService: VisitorPlaceService,
    public visitorService: VisitorService,
    private _router: Router,
    private _location: Location,
    private _store: Store<IAppState>
  ) {
    super({ activatedRoute });
    this.initialCustomPlacesId = this.readCustomPlacesIdFromUrl();
    this._store.pipe(select(frontendSettingsSelector)).subscribe((settings: IFrontendSettings) => {
      if (!settings) return;
      this.settings = settings;
      this.captureUrlRootNode();
    });

    // set title and selected outline
    this._store
      .pipe(takeUntil(this.isDestroyed), select(placeQueryCommandSelector))
      .subscribe((cmd: CalendarQueryCommand) => {
        // receive NgRx store to update the category dropdown on topbar/side. Nearby places component doesn't need this
        if (!cmd || !this.isTriggerCalendarReload) return;
        this.title = cmd.category?.toLowerCase() !== 'all' ? cmd.category : '';
        if (this.isOnlyShowNodeFromUrl) {
          this.node = cmd.categoryNode !== undefined ? cmd.categoryNode : this.node;
          this.captureUrlRootNode();
          if (!this.urlRootNode && this.node && !this.hasUserSelectedCategory) {
            this.urlRootNode = this.node;
          }
        }
        // this.isLoading = false;
      });

    // notify subscribers about the filter changes. Such as in event details page's nearby places component
    // here is to subscribe to the custom default category changes and set title accordingly
    PlaceStore.customDefaultCategoryStore
      .pipe(delay(100)) // delay to move this behind NgRx subscribe
      .subscribe((store) => {
        if (store && !this.isTriggerCalendarReload) {
          this.title = store.categoryName?.toLowerCase() !== 'all' ? store.categoryName : '';
          if (this.isOnlyShowNodeFromUrl) {
            this.node = store.categoryNode || this.node;
          }
        }
      });
  }

  public get isShowThingsToDo() {
    return (
      this.settings &&
      (this.settings.toggle.placeCategory.activity ||
        this.settings.toggle.placeCategory.art ||
        this.settings.toggle.placeCategory.outdoor ||
        this.settings.toggle.placeCategory.entertainment)
    );
  }

  public isSelected(title: string) {
    return false;
  }

  onSelectCustomDefaultPlaceCategory(node: PlaceType | null) {
    console.log('node', node);
    console.log('this.node', this.node);
    this.hasUserSelectedCategory = true;
    this.captureUrlRootNode();

    if (!node) {
      const restoreNode =
        this.isOnlyShowNodeFromUrl && this.urlRootNode ? this.urlRootNode : null;

      if (restoreNode) {
        this.applyCategoryNode(restoreNode);
        this.syncCustomPlacesUrl(restoreNode, { restorePrimary: true });
        return;
      }

      this.applyAllPlaces();
      this.syncCustomPlacesUrl(null);
      return;
    }

    this.applyCategoryNode(node);
    this.syncCustomPlacesUrl(node);
  }

  private applyCategoryNode(node: PlaceType) {
    let categoryList = [];
    this.placeService.getAllSubCategories(node, categoryList);
    this.node = node;
    this.title = node.title.toLowerCase() === 'all' ? '' : node.title;

    this.isTriggerCalendarReload &&
      this._store.dispatch(
        PlaceQueryCommandActions.setCustomDefaultCategoryList({
          categoryList: categoryList,
          categoryName: node.title,
          categoryNode: node,
          customCategoryId: this.placeService.getCustomCategoryId(node),
        })
      );

    PlaceStore.customDefaultCategoryStore.next({
      categoryList: categoryList,
      categoryName: node.title,
      category: node.title,
      categoryNode: node,
      customCategoryId: this.placeService.getCustomCategoryId(node),
    });
  }

  private applyAllPlaces() {
    this.title = '';
    this.node = null;

    this.isTriggerCalendarReload &&
      this._store.dispatch(
        PlaceQueryCommandActions.setCustomDefaultCategoryList({
          categoryList: undefined,
          customCategoryId: undefined,
          categoryNode: null,
        })
      );

    PlaceStore.customDefaultCategoryStore.next({
      categoryList: undefined,
      customCategoryId: undefined,
      categoryNode: null,
    });
  }

  private captureUrlRootNode() {
    if (!this.settings) return;
    const categories =
      this.settings.customSettings?.customDefaultPlaceCategory?.customCategories;
    if (!categories?.length) return;

    const categoryId = this.allRouteParams?.['categoryId'];
    if (categoryId) {
      const fromRoute = this.placeService.findCategoryById(categoryId, categories);
      if (fromRoute) {
        this.urlRootNode = fromRoute;
        return;
      }
    }

    if (!this.urlRootNode && this.initialCustomPlacesId) {
      this.urlRootNode = this.placeService.findCategoryById(
        this.initialCustomPlacesId,
        categories
      );
    }
  }

  private readCustomPlacesIdFromUrl(): string | null {
    if (typeof window === 'undefined' || !window.location.search.includes('ig-custom-places')) {
      return null;
    }
    const value = new URLSearchParams(window.location.search).get('ig-custom-places');
    if (!value) return null;
    return value.includes('-') ? value.split('-').pop() || value : value;
  }

  private syncCustomPlacesUrl(
    node: PlaceType | null,
    options?: { restorePrimary?: boolean }
  ) {
    // don't change URL for client dashboard
    const igDomain = [ConfigService.config.domainV3, ConfigService.config.domainV3Dev, 'localhost'];
    if (igDomain.some((x) => x.includes(window.location.hostname))) return;

    const currentPath = this._location.path();
    const shouldClearQuery =
      !node ||
      (options?.restorePrimary &&
        this.placeService.getCustomCategoryId(node) !== this.initialCustomPlacesId);

    if (shouldClearQuery) {
      if (currentPath.includes('ig-custom-places=')) {
        let originUrl = currentPath.split('ig-custom-places=')[0];
        originUrl =
          originUrl.endsWith('?') || originUrl.endsWith('&')
            ? originUrl.substring(0, originUrl.length - 1)
            : originUrl;
        this._location.go(originUrl);
      }
      return;
    }

    if (currentPath.includes('ig-custom-places=')) {
      this._location.go(
        currentPath.split('ig-custom-places=')[0] +
          `ig-custom-places=${this.visitorService.convertToUrlSafeString(node.title)}-${node._id}`
      );
      return;
    }
    const connector = currentPath.includes('?') ? '&' : '?';
    this._location.go(
      currentPath +
        `${connector}ig-custom-places=${this.visitorService.convertToUrlSafeString(node.title)}-${
          node._id
        }`
    );
  }

  filterPlace(category: string) {
    this.title = category.toLowerCase() === 'all' ? '' : category;
    this.isTriggerCalendarReload &&
      this._store.dispatch(
        PlaceQueryCommandActions.setDefaultCategory({
          category,
          categoryName: category,
        })
      );

    // have no idea why the above dispatch doesn't work in event details page's nearby places component
    // here we notify the nearby places component to load the places
    PlaceStore.customDefaultCategoryStore.next({
      category,
      categoryName: category,
    });

    this.pendoService.track(`Filter By Category  - ${category}`);
  }
}
